import { Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Receipt, RefreshCw } from "lucide-react";
import { api, fmt, fdatetime, fdate, errMsg } from "@/lib/api";

const LABELS = { received: "Received", in_production: "In production", ready: "Ready for collection", collected: "Delivered" };

function ReceiptCard({ d }) {
  return <section className="space-y-2 rounded-lg border border-slate-200 p-4" data-testid="track-receipt">
    <div className="flex items-center justify-between"><h2 className="flex items-center gap-1.5 font-semibold text-slate-900"><Receipt className="h-4 w-4" />Receipt</h2>
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${d.payment_confirmed ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="track-payment-status">{d.payment_confirmed ? "Payment confirmed" : "Payment pending"}</span></div>
    <ul className="divide-y divide-slate-100 text-sm text-slate-700">{d.items.map((it, i) => <li key={i} className="flex justify-between py-1"><span>{it.quantity} × {it.description}</span>{it.amount != null && <span>{fmt(it.amount, d.currency)}</span>}</li>)}</ul>
    <div className="flex justify-between border-t border-slate-100 pt-2 font-semibold"><span>Amount paid</span><span data-testid="track-paid">{fmt(d.paid, d.currency)}</span></div>
    <dl className="grid grid-cols-2 gap-1 text-xs text-slate-500">
      <dt>Date</dt><dd className="text-right">{fdatetime(d.created_at)}</dd>
      {d.branch && <><dt>Branch</dt><dd className="text-right" data-testid="track-branch">{d.branch}</dd></>}
      {d.cashier && <><dt>Cashier</dt><dd className="text-right" data-testid="track-cashier">{d.cashier}</dd></>}
      {d.payments.map((p, i) => <Fragment key={i}><dt>{p.label}</dt><dd className="text-right">{fmt(p.amount, d.currency)}</dd></Fragment>)}
    </dl>
  </section>;
}

function Progress({ d }) {
  const at = Object.fromEntries((d.history || []).map((h) => [h.status, h.at]));
  return <section className="space-y-3 rounded-lg border border-slate-200 p-4" data-testid="track-progress">
    <h2 className="font-semibold text-slate-900">Job progress</h2>
    <p className="text-xs text-slate-500" data-testid="track-eta">Estimated completion: {d.est_ready_at ? fdate(d.est_ready_at) : "to be confirmed"}</p>
    <ol className="space-y-2" data-testid="track-steps">{d.steps.map((s, i) => {
      const done = d.steps.indexOf(d.status) >= i;
      return <li key={s} className={`flex items-center gap-2 text-sm ${done ? "font-medium text-[#13294B]" : "text-slate-400"}`} data-testid={`track-step-${s}`}>
        <span className={`flex h-5 w-5 items-center justify-center rounded-full ${done ? "bg-[#45BFB4] text-white" : "border border-slate-300"}`}>{done && <Check className="h-3 w-3" />}</span>
        {LABELS[s] || s}{at[s] && <span className="ml-auto text-xs font-normal text-slate-400">{fdatetime(at[s])}</span>}</li>;
    })}</ol>
  </section>;
}

export default function Track() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    const load = () => api.get(`/track/${token}`).then((r) => { setD(r.data); setErr(""); }).catch((e) => setErr(errMsg(e)));
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [token]);
  return (
    <div className="flex min-h-screen items-start justify-center bg-slate-100 p-4 sm:p-6">
      <div className="w-full max-w-md space-y-4 rounded-lg bg-white p-5 shadow-sm" data-testid="track-page">
        <img src="/brand/logo.png" alt="PressOps" className="h-8" />
        {err && <p className="text-sm text-slate-600" data-testid="track-error">{err}</p>}
        {d && <>
          <div><div className="text-sm text-slate-500">{d.company}</div><h1 className="text-2xl font-bold text-slate-900" data-testid="track-number">{d.number}</h1>
            <div className="flex items-center gap-1 text-xs text-slate-400"><RefreshCw className="h-3 w-3" />Updates automatically · bookmark or share this page</div></div>
          <ReceiptCard d={d} />
          <Progress d={d} />
        </>}
      </div>
    </div>
  );
}
